import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { goiNapTokenRepository } from '../../../lib/repositories/goi-nap-token.repository';
import { useToast } from '../../../lib/toast';
import { thuLaiToken } from '../../tenant/hooks/useHanMucTokenAi';
import type {
  AiTokenModelUsage,
  AiTokenPricing,
  AiTokenPricingUpdate,
  TopUpCatalog,
  TopUpPack,
  TopUpPackCreate,
  TopUpPackUpdate,
} from '../types';

/**
 * Khoá cache của bán token Fox (ADR-0100). Đổi giá bán làm giá của gói nạp token và danh mục đang bán đổi theo, nên
 * ba khoá chung gốc `['ban-token']`.
 */
export const KHOA_BAN_TOKEN = {
  tatCa: ['ban-token'] as const,
  goi: ['ban-token', 'goi'] as const,
  dangBan: (tenantId: string) => ['ban-token', 'dang-ban', tenantId] as const,
  giaBan: ['ban-token', 'gia-ban'] as const,
  theoModel: (tenantId: string, thang?: string) => ['ban-token', 'theo-model', tenantId, thang ?? 'nay'] as const,
};

export function useGoiNapToken() {
  return useQuery<TopUpPack[], ApiError>({
    queryKey: KHOA_BAN_TOKEN.goi,
    queryFn: () => goiNapTokenRepository.danhSach(),
    staleTime: 60_000,
    retry: thuLaiToken,
  });
}

/** Lỗi không toast: hộp giữ dữ liệu đã nhập và hiện lỗi cạnh ô. */
export function useTaoGoiNap() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<TopUpPack, ApiError, { du: TopUpPackCreate; khoaLap: string }>({
    mutationFn: ({ du, khoaLap }) => goiNapTokenRepository.tao(du, khoaLap),
    onSuccess: (g) => {
      void qc.invalidateQueries({ queryKey: KHOA_BAN_TOKEN.tatCa });
      toast.success(t('banToken.goiNap.daTao', { ten: g.name }));
    },
  });
}

export function useSuaGoiNap() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<TopUpPack, ApiError, { code: string; du: TopUpPackUpdate }>({
    mutationFn: ({ code, du }) => goiNapTokenRepository.sua(code, du),
    onSuccess: (g, { du }) => {
      void qc.invalidateQueries({ queryKey: KHOA_BAN_TOKEN.tatCa });
      toast.success(
        du.is_active === undefined
          ? t('banToken.goiNap.daLuu', { ten: g.name })
          : t(du.is_active ? 'banToken.goiNap.daBat' : 'banToken.goiNap.daNgungBan', { ten: g.name }),
      );
    },
  });
}

/** Gói đang bán của tổ chức kèm giá, đơn giá, mức nạp gợi ý (hộp gửi yêu cầu nạp token). */
export function useGoiDangBan(tenantId: string | undefined) {
  return useQuery<TopUpCatalog, ApiError>({
    queryKey: KHOA_BAN_TOKEN.dangBan(tenantId ?? ''),
    queryFn: () => goiNapTokenRepository.dangBan(tenantId ?? ''),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
    retry: thuLaiToken,
  });
}

export function useGiaBanToken(batDau = true) {
  return useQuery<AiTokenPricing, ApiError>({
    queryKey: KHOA_BAN_TOKEN.giaBan,
    queryFn: () => goiNapTokenRepository.giaBan(),
    enabled: batDau,
    staleTime: 60_000,
    retry: thuLaiToken,
  });
}

export function useDatGiaBanToken() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<AiTokenPricing, ApiError, AiTokenPricingUpdate>({
    mutationFn: (du) => goiNapTokenRepository.datGiaBan(du),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KHOA_BAN_TOKEN.tatCa });
      toast.success(t('banToken.donGia.daLuu'));
    },
  });
}

/** Token thật và token Fox theo model của tháng (mặc định tháng này). */
export function useTokenTheoModel(tenantId: string | undefined, batDau = true, thang?: string) {
  return useQuery<AiTokenModelUsage, ApiError>({
    queryKey: KHOA_BAN_TOKEN.theoModel(tenantId ?? '', thang),
    queryFn: () => goiNapTokenRepository.theoModel(tenantId ?? '', thang),
    enabled: Boolean(tenantId) && batDau,
    staleTime: 30_000,
    retry: thuLaiToken,
  });
}
